import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import i18n from './config';

export type Locale = 'pt-BR' | 'en-US';

export const LOCALES: { value: Locale; label: string }[] = [
  { value: 'pt-BR', label: 'PT' },
  { value: 'en-US', label: 'EN' },
];

interface LocaleState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const customStringStorage: StateStorage = {
  getItem: (name: string): string | null => {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(name);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed?.state
        ? raw
        : JSON.stringify({ state: { locale: raw }, version: 0 });
    } catch {
      return JSON.stringify({ state: { locale: raw }, version: 0 });
    }
  },
  setItem: (name: string, value: string): void => {
    if (typeof localStorage === 'undefined') return;
    try {
      const parsed = JSON.parse(value);
      const loc = (parsed?.state?.locale as Locale) || 'pt-BR';
      localStorage.setItem(name, loc);
    } catch {
      localStorage.setItem(name, value);
    }
  },
  removeItem: (name: string): void => {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(name);
  },
};

export const useLocale = create<LocaleState>()(
  persist(
    (set) => ({
      locale:
        (typeof localStorage !== 'undefined' &&
          (localStorage.getItem('wiredesk_locale') as Locale)) ||
        'pt-BR',
      setLocale: (locale: Locale) => {
        void i18n.changeLanguage(locale);
        if (typeof document !== 'undefined') {
          document.documentElement.lang = locale;
        }
        set({ locale });
        // pos-DD-V (i18n real): o idioma escolhido deixa de morrer no
        // localStorage deste navegador — com sessão aberta, grava a
        // preferência do tenant via PUT /config/locale (endpoint que existia
        // montado e nunca foi consumido), para e-mails/recibos saírem no
        // idioma certo. Fire-and-forget de propósito: 403 de não-admin ou
        // rede caída degradam para o efeito local, sem travar o clique.
        // Imports dinâmicos para não criar ciclo useLocale <-> api.
        void (async () => {
          try {
            const { useAuth } = await import('@/store/useAuth');
            if (!useAuth.getState().token) return;
            const { api } = await import('@/services/api');
            // 400 = staff (sem tenant próprio, por desenho) · 403 = não-admin.
            // Ambos degradam para o efeito local SEM alarme global (F-486).
            await api.put('/config/locale', { idioma: locale }, { esperados: [400, 403] });
          } catch {
            /* efeito local basta */
          }
        })();
      },
    }),
    {
      name: 'wiredesk_locale',
      storage: createJSONStorage(() => customStringStorage),
      onRehydrateStorage: () => (state) => {
        if (state?.locale) {
          void i18n.changeLanguage(state.locale);
          if (typeof document !== 'undefined') {
            document.documentElement.lang = state.locale;
          }
        }
      },
    },
  ),
);
